import React, { memo, useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { remove } from 'lodash';
import { useIntl } from 'react-intl';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  CardActions,
  Link,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddCircleRoundedIcon from '@material-ui/icons/AddCircleRounded';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import { BrokerFavoriteListing, Listing, Shooting } from 'models';
import { images } from 'assets/images';
import { COLOR, SERVICE_OFFERS_PUBLIC_PAGE } from 'config/constants';

const useStyles = makeStyles({
  listingCard: {
    width: 280,
    height: 382,
    marginBottom: 15,
  },
  cover: {
    position: 'relative',
  },
  cardThumbnail: {
    height: 200,
  },
  address: {
    color: '#000',
    fontSize: 12,
    height: 30,
    marginBottom: 5,
    '& span': {
      fontWeight: 700,
    },
  },
  button: {
    borderRadius: '8px',
    fontFamily: 'Ford Antenna',
    alignItems: 'center',
    textTransform: 'none',
    marginRight: '8px',
    marginTop: 20,
    padding: '6px 15px',

    '& img': {
      marginRight: '5px',
    },
  },
  serviceOffersContent: {
    overflowX: 'auto',
  },
  serviceOfferIcon: {
    backgroundColor: 'white',
    display: 'flex',
    borderRadius: 5,
    boxShadow: '0px 3px 5px 0px rgba(0,0,0,0.15)',
    width: 25,
    height: 25,
    padding: 5,
    transition: 'boxShadow 1s ease-in-out',
    marginBottom: 5,
  },
  serviceOfferIconContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: 10,
  },
  serviceOffer: {
    display: 'flex',
    flexDirection: 'column',
    width: 50,
    marginRight: 10,
  },
  mediaDeliveryLink: {
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    height: 54,
    '& .MuiTypography-root': {
      fontFamily: 'FordAntenna-SemiBold',
      marginRight: 5,
      textTransform: 'uppercase',
    },
  },
  cardActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#1D2F66',
    height: 54,
  },
  row: {
    display: 'flex',
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: COLOR.ULTRA_LIGHT_GREY,
  },
  cardContent: {
    height: 80,
    backgroundColor: COLOR.ULTRA_LIGHT_GREY,
  },
  buttonText: {
    fontSize: 12,
  },
});

interface Props {
  className: string;
  selectedListings?: any;
  setSelectedListings?: (_: any) => void;
  setActiveStep?: (_: number) => void;
  previousStep?: number;
  listingWithServiceOffer?: BrokerFavoriteListing;
  favorite_listings: any;
}

function ListingCard(props: Props) {
  const styles = useStyles();

  const {
    className,
    selectedListings,
    setSelectedListings,
    setActiveStep,
    previousStep,
    listingWithServiceOffer,
    favorite_listings,
  } = props;
  const intl = useIntl();
  const picture = listingWithServiceOffer.thumbnail || images.logo;

  const [isSelected, setIsSelected] = useState(null);
  const [isChosen, setIsChosen] = useState(null);
  const fmtMediaPage = intl.formatMessage({ id: 'portal.mediaPage' });

  const service_offer_icons = {
    drone: images.drone_svg,
    blue_hour: images.blue_hour_svg,
    virtual_staging: images.virtualStaging,
    floorplan: images.floorplan,
    video: images.video,
    virtual_tour: images.video360,
    shooting_package: images.camera,
    matterport: images.matterport_svg,
  };
  useEffect(() => {
    const foundSelected = selectedListings.find((l) => {
      return l.listing_id === listingWithServiceOffer.listing_id;
    });
    const foundFavoriteListing = favorite_listings.find((l) => {
      return l.listing_id === listingWithServiceOffer.listing_id;
    });
    const isSelected = foundSelected;
    const isChosen = foundFavoriteListing;
    setIsSelected(isSelected);
    setIsChosen(isChosen);
  }, [isSelected, selectedListings]);

  const addListing = () => {
    setSelectedListings([...selectedListings, listingWithServiceOffer]);
  };

  const removeListing = () => {
    const newItems = remove(selectedListings, (item: BrokerFavoriteListing) => {
      return item.listing_id !== listingWithServiceOffer.listing_id;
    });
    setSelectedListings(newItems);
    if (!newItems.length) {
      if (previousStep !== undefined) {
        setActiveStep(previousStep);
      }
      setSelectedListings([]);
    }
  };
  const loadEditCardIcon = () => {
    if (isSelected) {
      return (
        <IconButton onClick={removeListing}>
          <RemoveCircleIcon style={{ fontSize: 30, color: COLOR.RED }} />
        </IconButton>
      );
    }
    return (
      <IconButton onClick={addListing}>
        <AddCircleRoundedIcon style={{ fontSize: 30, color: COLOR.LIGHT_BLUE }} />
      </IconButton>
    );
  };
  const loadServiceOffers = () => {
    return (
      <Box className={styles.serviceOfferIconContainer}>
        {listingWithServiceOffer.service_offers.map((service_offer) => {
          let service_offer_icon = null;
          let service_offer_name = null;

          for (const key in SERVICE_OFFERS_PUBLIC_PAGE) {
            if (SERVICE_OFFERS_PUBLIC_PAGE[key].includes(service_offer.id)) {
              service_offer_icon = service_offer_icons[key];
              service_offer_name = intl.formatMessage({ id: `portal.serviceOffer_${key}` });
              break;
            }
          }

          if (service_offer_icon === null) {
            return null;
          }
          return (
            <Box key={service_offer.id} className={styles.serviceOffer}>
              <Tooltip title={service_offer_name}>
                <Box className={styles.serviceOfferIcon}>
                  <img width={25} height={25} src={service_offer_icon} alt='service offer icon' />
                </Box>
              </Tooltip>
            </Box>
          );
        })}
      </Box>
    );
  };
  if (isChosen) {
    return null;
  }
  return (
    <Card className={classNames(className, styles.listingCard)} variant='outlined'>
      <Box className={styles.cover}>
        <CardMedia
          className={styles.cardThumbnail}
          image={picture}
          style={{ cursor: 'pointer' }}
          title='House picture'
        />
      </Box>
      <CardContent className={styles.cardContent}>
        <Box>
          <Typography className={styles.address}>{listingWithServiceOffer.address}</Typography>
          <Box className={styles.serviceOffersContent}>
            {listingWithServiceOffer?.service_offers && loadServiceOffers()}
          </Box>
        </Box>
      </CardContent>
      <CardActions className={styles.cardActions}>
        {loadEditCardIcon()}
        <Link
          component='button'
          className={styles.mediaDeliveryLink}
          onClick={() => {
            window.open(
              `https://media.hausvalet.ca/delivery/${listingWithServiceOffer.listing.distribution_token}`,
              '_blank',
            );
          }}
        >
          <Typography className={styles.buttonText}>{fmtMediaPage}</Typography>
          <img src={images.link} width={20} height={20} alt='redirection icon' />
        </Link>
      </CardActions>
    </Card>
  );
}

ListingCard.defaultProps = {
  className: null,
};

ListingCard.propTypes = {
  className: PropTypes.string,
};

export default ListingCard;
